import { Channel } from '../model/skill';
import { ThereminvoxSoundMeta, InnerSoundMetaState, InnerSoundMeta, DropResult } from '../model/thereminvox';
import { useSynchronizedData } from '../hooks/useSynchronizedData';

interface SkillEntity {
    channel: Channel;
    hideInStore: boolean;
}

export const isPrivateThereminvoxSkill = (skill: SkillEntity) => {
    return skill.channel === Channel.Thereminvox && skill.hideInStore;
};

export const useInnerSoundsMeta = (
    soundsMeta: ThereminvoxSoundMeta[],
    onSoundsMetaChange: (soundsMeta: ThereminvoxSoundMeta[]) => void,
) => {
    const preprocess = (sMeta: ThereminvoxSoundMeta[]) =>
        sMeta.map((meta) => ({
            outerMeta: meta,
            state: 'uploaded' as InnerSoundMetaState,
            id: meta.id,
        })) as InnerSoundMeta[];

    const [innerSoundsMeta, setInnerSoundsMeta] = useSynchronizedData({
        data: soundsMeta,
        onChange: onSoundsMetaChange,
        preprocess,
        postprocess: (innerSMeta) =>
            innerSMeta.filter((meta) => Boolean(meta.outerMeta)).map((meta) => meta!.outerMeta!),
    });

    const hasUploadingSounds = innerSoundsMeta.some((meta) => meta.state === 'uploading');
    const allUploaded = innerSoundsMeta.every((meta) => meta.state === 'uploaded');
    const hasMaxSounds = innerSoundsMeta.length === __appConfig.thereminvox.maxSounds;
    const hasErrors = innerSoundsMeta.some((meta) => meta.state === 'error');

    return {
        innerSoundsMeta,
        setInnerSoundsMeta,
        hasUploadingSounds,
        allUploaded,
        hasMaxSounds,
        hasErrors,
    };
};

export const stopAllSoundsIn = ({ container }: { container: HTMLElement }) => {
    const audios = container.querySelectorAll('audio');

    audios.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
    });
};

export const applyDrag = <T>(arr: T[], dragResult: DropResult<T>) => {
    const { removedIndex, addedIndex, payload } = dragResult;

    if (removedIndex === null && addedIndex === null) {
        return arr;
    }

    const result = [...arr];
    let itemToAdd = payload;

    if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0];
    }

    if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd);
    }

    return result;
};

export const isThereminvoxEnabled = () => {
    return (
        __appConfig.thereminvox.enabled ||
        __appConfig.user.featureFlags.allowCreateThereminvoxSkills ||
        __appConfig.user.isYandexoid
    );
};
